import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

// Initial booking state
const initialBookingState = {
  // Service selection
  serviceType: null, // service ID
  serviceName: null, // human-readable service name
  cleaningOption: null,
  extraServices: [], // array of extra service IDs
  
  // Property details
  propertyDetails: {
    address: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: 'house', // 'house', 'apartment', 'condo', 'office'
    squareFootage: 1000,
    bedrooms: 2,
    bathrooms: 2,
    floors: 1,
    pets: false,
    specialInstructions: ''
  },
  
  // Scheduling
  scheduling: {
    serviceDate: '',
    serviceTime: '',
    frequency: 'one-time', // 'one-time', 'weekly', 'bi-weekly', 'monthly'
    flexibleDates: false
  },
  
  // Customer information
  customerInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirm: '',
    zipCode: '',
    contactPreference: 'email' // 'email', 'phone', 'text'
  },
  
  // Payment
  payment: {
    method: null, // 'card', 'voucher'
    voucherCode: '',
    estimatedPrice: 0,
    finalPrice: 0,
    // Booking operation status tracking
    bookingOperationStatus: 'pending', // 'pending', 'success', 'failed'
    bookingOperationFailed: false,
    bookingOperationError: null
  },
  
  // Flow management
  currentStep: 0,
  completedSteps: [],
  isDraft: true,
  isSubmitting: false,
  errors: {},
  
  // User creation tracking
  leadUserCreated: false,
  leadUserId: null,
  isGuestMode: false, // True for guest checkout without account creation
  
  // Booking management
  bookingId: null,
  OrderID: null,
  
  // Flow management
  flowStartedAsGuest: true,

  // Metadata
  createdAt: null,
  lastModified: null
};

// Action types
const BOOKING_ACTIONS = {
  SET_FLOW_STARTED_AS_GUEST: 'SET_FLOW_STARTED_AS_GUEST',
  SET_GUEST_MODE: 'SET_GUEST_MODE',
  SET_SERVICE_TYPE: 'SET_SERVICE_TYPE',
  SET_EXTRA_SERVICES: 'SET_EXTRA_SERVICES',
  UPDATE_PROPERTY_DETAILS: 'UPDATE_PROPERTY_DETAILS',
  UPDATE_SCHEDULING: 'UPDATE_SCHEDULING',
  UPDATE_CUSTOMER_INFO: 'UPDATE_CUSTOMER_INFO',
  UPDATE_PAYMENT: 'UPDATE_PAYMENT',
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  MARK_STEP_COMPLETED: 'MARK_STEP_COMPLETED',
  SET_ERRORS: 'SET_ERRORS',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_SUBMITTING: 'SET_SUBMITTING',
  RESET_BOOKING: 'RESET_BOOKING',
  UPDATE_LAST_MODIFIED: 'UPDATE_LAST_MODIFIED',
  SET_LEAD_USER_CREATED: 'SET_LEAD_USER_CREATED',
  SET_BOOKING_ID: 'SET_BOOKING_ID'
};

// Reducer function
const bookingReducer = (state, action) => {
  switch (action.type) {
    case BOOKING_ACTIONS.SET_SERVICE_TYPE:
      return {
        ...state,
        serviceType: action.payload.serviceType,
        serviceName: action.payload.serviceName || null,
        cleaningOption: action.payload.cleaningOption || null,
        lastModified: new Date().toISOString()
      };
      
    case BOOKING_ACTIONS.SET_EXTRA_SERVICES:
      return {
        ...state,
        extraServices: action.payload,
        lastModified: new Date().toISOString()
      };
      
    case BOOKING_ACTIONS.UPDATE_PROPERTY_DETAILS:
      return {
        ...state,
        propertyDetails: { ...state.propertyDetails, ...action.payload },
        lastModified: new Date().toISOString()
      };
      
    case BOOKING_ACTIONS.UPDATE_SCHEDULING:
      return {
        ...state,
        scheduling: { ...state.scheduling, ...action.payload },
        lastModified: new Date().toISOString()
      };
      
    case BOOKING_ACTIONS.UPDATE_CUSTOMER_INFO:
      return {
        ...state,
        customerInfo: { ...state.customerInfo, ...action.payload },
        lastModified: new Date().toISOString()
      };
      
    case BOOKING_ACTIONS.UPDATE_PAYMENT:
      return {
        ...state,
        payment: { 
          ...state.payment, 
          ...action.payload,
          // Update booking operation status based on payload
          bookingOperationStatus: action.payload.bookingCreated ? 'success' : 
                                   action.payload.bookingOperationFailed ? 'failed' : 
                                   state.payment.bookingOperationStatus
        },
        // Set isDraft to false when booking is completed
        isDraft: action.payload.bookingCreated ? false : state.isDraft,
        lastModified: new Date().toISOString()
      };
      
    case BOOKING_ACTIONS.SET_CURRENT_STEP:
      console.log('🎛️ REDUCER: SET_CURRENT_STEP:', {
        oldStep: state.currentStep,
        newStep: action.payload,
        stepChange: `${state.currentStep} → ${action.payload}`
      });
      return {
        ...state,
        currentStep: action.payload
      };
      
    case BOOKING_ACTIONS.MARK_STEP_COMPLETED:
      return {
        ...state,
        completedSteps: [...new Set([...state.completedSteps, action.payload])]
      };
      
    case BOOKING_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: { ...state.errors, ...action.payload }
      };
      
    case BOOKING_ACTIONS.CLEAR_ERRORS:
      return {
        ...state,
        errors: action.payload ? 
          Object.keys(state.errors).reduce((acc, key) => {
            if (!action.payload.includes(key)) acc[key] = state.errors[key];
            return acc;
          }, {}) : {}
      };
      
    case BOOKING_ACTIONS.SET_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.payload
      };
      
    case BOOKING_ACTIONS.RESET_BOOKING:
      return {
        ...initialBookingState,
        // Preserve guest mode and flow settings when resetting
        flowStartedAsGuest: state.flowStartedAsGuest,
        isGuestMode: state.isGuestMode,
        createdAt: new Date().toISOString()
      };
      
    case BOOKING_ACTIONS.UPDATE_LAST_MODIFIED:
      return {
        ...state,
        lastModified: new Date().toISOString(),
        ...(action.payload?.OrderID && { OrderID: action.payload.OrderID })
      };
      
    case BOOKING_ACTIONS.SET_LEAD_USER_CREATED:
      return {
        ...state,
        leadUserCreated: action.payload.created,
        leadUserId: action.payload.userId || state.leadUserId
      };
      
    case BOOKING_ACTIONS.SET_FLOW_STARTED_AS_GUEST:
      return {
        ...state,
        flowStartedAsGuest: action.payload
      };
      
    case BOOKING_ACTIONS.SET_GUEST_MODE:
      return {
        ...state,
        isGuestMode: action.payload
      };
      
    case BOOKING_ACTIONS.SET_BOOKING_ID:
      return {
        ...state,
        bookingId: action.payload.bookingId,
        OrderID: action.payload.OrderID || state.OrderID
      };
      
    default:
      return state;
  }
};

// Create context
const BookingContext = createContext();

// Provider component
export const BookingProvider = ({ children }) => {
  const { user, clientProfile, isAuthenticated, createUser } = useAuth();
  const [bookingState, dispatch] = useReducer(bookingReducer, {
    ...initialBookingState,
    // Set flowStartedAsGuest to false if user is already authenticated at start
    flowStartedAsGuest: !user,
    // Default to guest mode for new flows
    isGuestMode: !user,
    createdAt: new Date().toISOString()
  });

  // Memoized available steps based on initial flow configuration
  const availableSteps = useMemo(() => {
    const steps = [];

    // If the flow started as a guest, include either guest or customer step
    if (bookingState.flowStartedAsGuest) {
      if (bookingState.isGuestMode) {
        steps.push({
          key: 'guest',
          title: 'Your Information',
          description: 'Tell us about yourself'
        });
      } else {
        steps.push({
          key: 'customer',
          title: 'Your Information',
          description: 'Tell us about yourself'
        });
      }

      steps.push({
        key: 'property',
        title: 'Property Details',
        description: 'Tell us about your property'
      });
    }

    // Always include service selection step
    steps.push({
      key: 'service',
      title: 'Service Selection',
      description: 'Choose your cleaning service'
    });

    // Always include scheduling step
    steps.push({
      key: 'scheduling',
      title: 'Schedule',
      description: 'Pick your preferred date and time'
    });

    // Always include payment step
    steps.push({
      key: 'payment',
      title: 'Payment',
      description: 'Complete your booking'
    });

    // Always include confirmation step
    steps.push({
      key: 'confirmation',
      title: 'Confirmation',
      description: 'Booking confirmed'
    });

    return steps;
  }, [bookingState.flowStartedAsGuest, bookingState.isGuestMode]);

  // Reset booking state when user logs out
  useEffect(() => {
    if (!user) {
      dispatch({ type: BOOKING_ACTIONS.RESET_BOOKING });
    }
  }, [user]);

  // Pre-fill customer info for authenticated users
  useEffect(() => {
    if (user && !bookingState.customerInfo.firstName) {
      dispatch({
        type: BOOKING_ACTIONS.UPDATE_CUSTOMER_INFO,
        payload: {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phoneNumber || user.phone || ''
        }
      });

      // Pre-fill property details if available from user profile
      if (user.address) {
        dispatch({
          type: BOOKING_ACTIONS.UPDATE_PROPERTY_DETAILS,
          payload: {
            address: user.address || '',
            city: user.city || '',
            state: user.state || '',
            zipCode: user.zipCode || ''
          }
        });
      }

      // User is authenticated - they can manually continue from the customer info step
      // Removed automatic step completion to give users control
    }
  }, [user, bookingState.customerInfo.firstName, availableSteps]);

  // Pre-fill from client profile when available
  useEffect(() => {
    if (clientProfile && user) {
      // Pre-fill scheduling preferences
      if (clientProfile.preferences && !bookingState.scheduling.frequency) {
        const schedulingUpdates = {};
        
        if (clientProfile.preferences.schedulingFrequency) {
          schedulingUpdates.frequency = clientProfile.preferences.schedulingFrequency;
        }
        
        if (clientProfile.preferences.preferredServiceTime) {
          schedulingUpdates.serviceTime = clientProfile.preferences.preferredServiceTime;
        }
        
        if (clientProfile.preferences.flexibleDates !== undefined) {
          schedulingUpdates.flexibleDates = clientProfile.preferences.flexibleDates;
        }
        
        if (Object.keys(schedulingUpdates).length > 0) {
          dispatch({
            type: BOOKING_ACTIONS.UPDATE_SCHEDULING,
            payload: schedulingUpdates
          });
        }
      }

      // Pre-fill property details from client profile if available
      if (clientProfile.address && !bookingState.propertyDetails.address) {
        dispatch({
          type: BOOKING_ACTIONS.UPDATE_PROPERTY_DETAILS,
          payload: {
            address: clientProfile.address || '',
            city: clientProfile.city || '',
            state: clientProfile.state || '',
            zipCode: clientProfile.zipCode || ''
          }
        });
      }
    }
  }, [clientProfile, user, bookingState.scheduling.frequency, bookingState.propertyDetails.address]);

  // Auto-create lead user when minimal contact info is complete
  useEffect(() => {
    const createLeadUser = async () => {
      const { firstName, lastName, email, phone, password, passwordConfirm, zipCode } = bookingState.customerInfo;
      
      // Check if we have basic contact info and haven't created user yet
      // Only create user if not in guest mode
      if (
        firstName && lastName && email && phone && password && passwordConfirm && 
        zipCode && zipCode.length === 5 && // ZIP code must be exactly 5 digits
        password === passwordConfirm && // Passwords must match
        !bookingState.leadUserCreated && 
        !user && // Only for non-authenticated users
        !bookingState.isGuestMode && // Don't auto-create users in guest mode
        bookingState.isDraft
      ) {
        try {
          const userData = {
            firstName,
            lastName,
            email,
            password,
            phone,
            zipCode: zipCode,
            city: bookingState.propertyDetails.city || '',
            state: bookingState.propertyDetails.state || '',
            address: bookingState.propertyDetails.address || '',
            message: 'User created from booking flow'
          };

          // Use AuthContext's createUser method to create and authenticate user
          const user = await createUser(userData);
          
          dispatch({
            type: BOOKING_ACTIONS.SET_LEAD_USER_CREATED,
            payload: { created: true, userId: user.id }
          });
          
          // User created successfully - they can manually continue to the next step
          // Removed automatic step completion to give users control
          
          console.log('✅ User created and authenticated successfully:', user.email);
        } catch (error) {
          // Don't show errors to user unless it's a duplicate email
          if (error.response?.data?.error === 'Email already registered') {
            dispatch({
              type: BOOKING_ACTIONS.SET_LEAD_USER_CREATED,
              payload: { created: true }
            });
            console.log('✅ User already exists, proceeding with booking');
          } else {
            console.warn('⚠️ Error creating user:', error.response?.data?.error || error.message);
          }
        }
      }
    };

    // Debounce the user creation to avoid multiple calls
    const timeoutId = setTimeout(createLeadUser, 2000); // 2 second debounce
    return () => clearTimeout(timeoutId);
  }, [
    bookingState.customerInfo.firstName,
    bookingState.customerInfo.lastName, 
    bookingState.customerInfo.email,
    bookingState.customerInfo.phone,
    bookingState.customerInfo.password,
    bookingState.customerInfo.passwordConfirm,
    bookingState.customerInfo.zipCode,
    bookingState.leadUserCreated,
    user,
    bookingState.isDraft,
    bookingState.isGuestMode,
    availableSteps,
    bookingState.propertyDetails.city,
    bookingState.propertyDetails.state,
    bookingState.propertyDetails.address,
    createUser
  ]);

  // Booking-state methods
  const saveAsBooking = useCallback(async () => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    try {
      const token = localStorage.getItem('token');
      
      // Clean the booking state before sending (backend now properly handles null values)
      const cleanedBookingState = {
        ...bookingState,
        scheduling: {
          ...bookingState.scheduling,
          serviceTime: bookingState.scheduling?.serviceTime && 
                      bookingState.scheduling.serviceTime !== 'Invalid date' 
                      ? bookingState.scheduling.serviceTime 
                      : null,
          serviceDate: bookingState.scheduling?.serviceDate && 
                      bookingState.scheduling.serviceDate !== 'Invalid date'
                      ? bookingState.scheduling.serviceDate 
                      : null
        }
      };
      
      if (bookingState.bookingId) {
        // Update existing NON_COMMITTED booking
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${bookingState.bookingId}/draft`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(cleanedBookingState)
        });
        
        const result = await response.json();
        if (result.success) {
          console.log('✅ Booking updated:', bookingState.bookingId);
        }
        return result;
      } else {
        // Create new NON_COMMITTED booking
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/create-booking`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...cleanedBookingState,
            status: 'NON_COMMITTED'
          })
        });
        
        const booking = await response.json();
        if (booking.id) {
          // Update context with booking ID and OrderID
          dispatch({
            type: BOOKING_ACTIONS.SET_BOOKING_ID,
            payload: { 
              bookingId: booking.id,
              OrderID: booking.OrderID 
            }
          });
          console.log('✅ Booking created:', booking.id, 'OrderID:', booking.OrderID);
        }
        
        return { success: true, booking };
      }
    } catch (error) {
      console.error('Failed to save as booking:', error);
      return { success: false, error: error.message };
    }
  }, [user, bookingState]);

  const commitBooking = useCallback(async (paymentIntentId) => {
    if (!user || !bookingState.bookingId) {
      return { success: false, error: 'User not authenticated or no booking to commit' };
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${bookingState.bookingId}/commit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paymentIntentId })
      });

      const result = await response.json();
      if (result.success) {
        // Reset context after successful commit
        //dispatch({ type: BOOKING_ACTIONS.RESET_BOOKING });
        console.log('✅ Booking committed:', result.booking.id);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to commit booking:', error);
      return { success: false, error: error.message };
    }
  }, [user, bookingState.bookingId]);

  const loadUserBookings = useCallback(async () => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/booking-drafts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Failed to get user bookings:', error);
      return { success: false, error: error.message };
    }
  }, [user]);

  // Auto-save when context changes (authenticated users only)
  useEffect(() => {
    const autoSave = async () => {
      // Auto-save for draft bookings only
      if (user && 
          bookingState.isDraft && 
          bookingState.lastModified && 
          !bookingState.payment.bookingCreated) {
        try {
          await saveAsBooking();
          console.log('📄 Booking auto-saved');
        } catch (error) {
          // Handle "already committed" errors gracefully - they're expected after payment
          if (error.message?.includes('already committed') || error.message?.includes('not found')) {
            console.log('📄 Auto-save skipped - booking already committed');
          } else {
            console.warn('Failed to auto-save:', error);
          }
        }
      } else {
        // Log why auto-save was skipped for debugging
        if (!user) {
          console.log('📄 Auto-save skipped - no user');
        } else if (!bookingState.isDraft) {
          console.log('📄 Auto-save skipped - not a draft (isDraft: false)');
        } else if (!bookingState.lastModified) {
          console.log('📄 Auto-save skipped - no lastModified');
        } else if (bookingState.payment.bookingCreated) {
          console.log('📄 Auto-save skipped - booking already created');
        }
      }
    };

    // Debounce auto-save by 2 seconds
    const timeoutId = setTimeout(autoSave, 2000);
    return () => clearTimeout(timeoutId);
  }, [user, bookingState, saveAsBooking]);


  // Helper functions
  const setServiceType = (serviceType, cleaningOption = null, serviceName = null) => {
    dispatch({
      type: BOOKING_ACTIONS.SET_SERVICE_TYPE,
      payload: { serviceType, cleaningOption, serviceName }
    });
  };

  const setExtraServices = (extraServices) => {
    dispatch({
      type: BOOKING_ACTIONS.SET_EXTRA_SERVICES,
      payload: extraServices
    });
  };

  const updatePropertyDetails = (updates) => {
    dispatch({
      type: BOOKING_ACTIONS.UPDATE_PROPERTY_DETAILS,
      payload: updates
    });
  };

  const updateScheduling = (updates) => {
    dispatch({
      type: BOOKING_ACTIONS.UPDATE_SCHEDULING,
      payload: updates
    });
  };

  const updateCustomerInfo = (updates) => {
    dispatch({
      type: BOOKING_ACTIONS.UPDATE_CUSTOMER_INFO,
      payload: updates
    });
  };

  const updatePayment = (updates) => {
    dispatch({
      type: BOOKING_ACTIONS.UPDATE_PAYMENT,
      payload: updates
    });
  };

  const setCurrentStep = (stepIndex) => {
    console.log('🔄 CONTEXT: setCurrentStep called:', {
      currentStep: bookingState.currentStep,
      targetStep: stepIndex,
      stepChange: `${bookingState.currentStep} → ${stepIndex}`,
      availableSteps: availableSteps.map((s, i) => `${i}: ${s.key}`)
    });
    
    dispatch({
      type: BOOKING_ACTIONS.SET_CURRENT_STEP,
      payload: stepIndex
    });
  };

  const markStepCompleted = (stepIndex) => {
    dispatch({
      type: BOOKING_ACTIONS.MARK_STEP_COMPLETED,
      payload: stepIndex
    });
  };

  const setErrors = (errors) => {
    dispatch({
      type: BOOKING_ACTIONS.SET_ERRORS,
      payload: errors
    });
  };

  const clearErrors = (fields = null) => {
    dispatch({
      type: BOOKING_ACTIONS.CLEAR_ERRORS,
      payload: fields
    });
  };

  const setSubmitting = (isSubmitting) => {
    dispatch({
      type: BOOKING_ACTIONS.SET_SUBMITTING,
      payload: isSubmitting
    });
  };

  const resetBooking = () => {
    dispatch({
      type: BOOKING_ACTIONS.RESET_BOOKING
    });
  };

  const setFlowStartedAsGuest = (value) => {
    dispatch({
      type: BOOKING_ACTIONS.SET_FLOW_STARTED_AS_GUEST,
      payload: value
    });
  };

  const setGuestMode = (value) => {
    dispatch({
      type: BOOKING_ACTIONS.SET_GUEST_MODE,
      payload: value
    });
  };

  const updateOrderID = (orderID) => {
    dispatch({
      type: BOOKING_ACTIONS.UPDATE_LAST_MODIFIED,
      payload: { OrderID: orderID }
    });
  };

  const retryBookingOperation = useCallback(async (paymentIntentId) => {
    if (!user || !paymentIntentId) {
      return { success: false, error: 'User not authenticated or missing payment intent' };
    }
    
    try {
      // Reset booking operation status to pending
      dispatch({
        type: BOOKING_ACTIONS.UPDATE_PAYMENT,
        payload: {
          bookingOperationStatus: 'pending',
          bookingOperationFailed: false,
          bookingOperationError: null
        }
      });
      
      let booking;
      
      // Ensure booking is saved and commit it
      if (!bookingState.bookingId) {
        const saveResult = await saveAsBooking();
        if (!saveResult.success) {
          throw new Error('Failed to save booking before payment');
        }
      }
      
      // Commit the booking with payment information
      const commitResult = await commitBooking(paymentIntentId);
      
      if (!commitResult.success) {
        throw new Error(commitResult.error || 'Failed to commit booking');
      }
      
      booking = commitResult.booking;
      console.log('✅ Booking operation retry successful', booking);
      
      // Update OrderID at root level (not in payment object)
      updateOrderID(booking.OrderID || booking.orderId || booking.id);
      
      // Update payment with successful booking info
      dispatch({
        type: BOOKING_ACTIONS.UPDATE_PAYMENT,
        payload: {
          bookingId: booking.id,
          bookingCreated: true,
          bookingOperationStatus: 'success',
          bookingOperationFailed: false,
          bookingOperationError: null
        }
      });
      
      return { success: true, booking };
    } catch (error) {
      console.error('⚠️ Booking operation retry failed:', error);
      
      // Update payment status to indicate retry failed
      dispatch({
        type: BOOKING_ACTIONS.UPDATE_PAYMENT,
        payload: {
          bookingOperationStatus: 'failed',
          bookingOperationFailed: true,
          bookingOperationError: error.message
        }
      });
      
      return { success: false, error: error.message };
    }
  }, [user, bookingState.bookingId, saveAsBooking, commitBooking]);



  // Validate current step
  const validateCurrentStep = (isAuthenticated = false) => {
    const steps = availableSteps;
    const currentStepKey = steps[bookingState.currentStep]?.key;
    const errors = {};

    switch (currentStepKey) {
      case 'service':
        if (!bookingState.serviceType) errors.serviceType = 'Please select a service type';
        break;
        
      case 'scheduling':
        if (!bookingState.scheduling.serviceDate) errors.serviceDate = 'Service date is required';
        if (!bookingState.scheduling.serviceTime) errors.serviceTime = 'Service time is required';
        break;
        
      case 'guest':
        // Simplified validation for guest users - no password required
        if (!bookingState.customerInfo.firstName) errors.firstName = 'First name is required';
        if (!bookingState.customerInfo.lastName) errors.lastName = 'Last name is required';
        if (!bookingState.customerInfo.email) errors.email = 'Email is required';
        if (!bookingState.customerInfo.phone) errors.phone = 'Phone number is required';
        if (!bookingState.customerInfo.zipCode) errors.zipCode = 'ZIP code is required';
        else if (!/^\d{5}$/.test(bookingState.customerInfo.zipCode)) errors.zipCode = 'Please enter a valid 5-digit ZIP code';
        break;
        
      case 'customer':
        // Skip validation if user is authenticated
        if (isAuthenticated) break;
        
        if (!bookingState.customerInfo.firstName) errors.firstName = 'First name is required';
        if (!bookingState.customerInfo.lastName) errors.lastName = 'Last name is required';
        if (!bookingState.customerInfo.email) errors.email = 'Email is required';
        if (!bookingState.customerInfo.phone) errors.phone = 'Phone number is required';
        if (!bookingState.customerInfo.password) errors.password = 'Password is required';
        else if (bookingState.customerInfo.password.length < 6) errors.password = 'Password must be at least 6 characters';
        if (!bookingState.customerInfo.passwordConfirm) errors.passwordConfirm = 'Please confirm your password';
        else if (bookingState.customerInfo.password !== bookingState.customerInfo.passwordConfirm) errors.passwordConfirm = 'Passwords do not match';
        if (!bookingState.customerInfo.zipCode) errors.zipCode = 'ZIP code is required';
        else if (!/^\d{5}$/.test(bookingState.customerInfo.zipCode)) errors.zipCode = 'Please enter a valid 5-digit ZIP code';
        break;
        
      case 'property':
        if (!bookingState.propertyDetails.address) errors.address = 'Address is required';
        if (!bookingState.propertyDetails.city) errors.city = 'City is required';
        if (!bookingState.propertyDetails.zipCode) errors.zipCode = 'ZIP code is required';
        if (!bookingState.propertyDetails.propertyType) errors.propertyType = 'Property type is required';
        break;
        
      case 'payment':
        if (!bookingState.payment.method) errors.paymentMethod = 'Please select a payment method';
        break;
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };


  const value = useMemo(() => ({
    // State
    bookingState,
    
    // Actions
    setServiceType,
    setExtraServices,
    updatePropertyDetails,
    updateScheduling,
    updateCustomerInfo,
    updatePayment,
    setCurrentStep,
    markStepCompleted,
    setErrors,
    clearErrors,
    setSubmitting,
    resetBooking,
    setFlowStartedAsGuest,
    setGuestMode,
    
    // Booking management
    saveAsBooking,
    commitBooking,
    loadUserBookings,
    retryBookingOperation,
    
    // Helpers
    availableSteps,
    validateCurrentStep,
    
    // Computed properties
    isAuthenticated: !!user,
    currentStepData: availableSteps[bookingState.currentStep],
    totalSteps: availableSteps.length,
    progressPercentage: Math.round((bookingState.currentStep / availableSteps.length) * 100)
  }), [
    bookingState,
    setServiceType,
    setExtraServices,
    updatePropertyDetails,
    updateScheduling,
    updateCustomerInfo,
    updatePayment,
    setCurrentStep,
    markStepCompleted,
    setErrors,
    clearErrors,
    setSubmitting,
    resetBooking,
    setFlowStartedAsGuest,
    setGuestMode,
    saveAsBooking,
    commitBooking,
    loadUserBookings,
    retryBookingOperation,
    availableSteps,
    validateCurrentStep,
    user
  ]);

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

// Hook to use booking context
export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};

export default BookingContext;